import { createSlice } from '@reduxjs/toolkit';
import {
  TeamTO,
  toTeamTO,
  toTenancyTO,
  type ClinicTO,
  type TenancyTO,
} from '../../lib';
import type { CaremapprClinicians } from './helpers';

interface ClinicianProfileState {
  firstName: string;
  lastName: string;
  accessToken: string;
  clinicianId: number | null;
  clinics: ClinicTO[];
  healthProfessionals: any[];
  primaryTeams: any[];
  teams: TeamTO[];
  tenancies: TenancyTO[];
  message: string | null;
  clinicianProfileStatus: boolean;
  selectedTenancy: TenancyTO | null;
  dashSettings: any;
  allUsers: Record<number, CaremapprClinicians[]>;
  allTenancyClinics: ClinicTO[];
  permissionsSetup: boolean;
  userPermissions: any;
  details: Record<string, any>;
}

const loadState = (): Partial<ClinicianProfileState> | undefined => {
  try {
    const serializedState = localStorage.getItem('clinicianProfileState');
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    console.error('Could not load state', err);
    return undefined;
  }
};

const persistedState = loadState();

const initialState: ClinicianProfileState = {
  firstName: '',
  lastName: '',
  accessToken: '',
  clinicianId: null,
  clinics: [],
  healthProfessionals: [],
  primaryTeams: [],
  teams: [],
  tenancies: [],
  message: null,
  clinicianProfileStatus: false,
  selectedTenancy: null,
  dashSettings: persistedState?.dashSettings || {},
  allUsers: {},
  allTenancyClinics: [],
  permissionsSetup: false,
  userPermissions: {},
  details: {},
};

const filterClinicsByTenancy = (clinics: ClinicTO[], tenancyId: number) => {
  return clinics.filter((clinic) => clinic.tenancy_id === tenancyId);
};

const getHealthProfessionalsFromClinics = (clinics: ClinicTO[]) => {
  const filteredHealthProfessionals = new Set<string>();
  clinics.forEach((clinic) => {
    if (clinic.healthProfessionals) {
      clinic.healthProfessionals.forEach((healthProfessional) => {
        filteredHealthProfessionals.add(healthProfessional);
      });
    }
  });
  return Array.from(filteredHealthProfessionals);
};

const getPrimaryTeamsFromClinics = (clinics: ClinicTO[]) => {
  const primaryTeams = clinics.flatMap(
    (clinic) =>
      clinic.primaryTeams?.flatMap((team) =>
        team.includes('/') ? team.split('/').map((t) => t.trim()) : [team],
      ) || [],
  );
  return Array.from(new Set(primaryTeams));
};

export const clinicianProfileBasicSlice = createSlice({
  name: 'clinicianProfileBasic',
  initialState,
  reducers: {
    checkingClinicianProfileStatus: (state) => {
      state.clinicianProfileStatus = false;
    },
    setClinicianProfile: (
      state,
      { payload }: { payload: Partial<ClinicianProfileState> },
    ) => {
      state.tenancies = (payload.tenancies || state.tenancies || []).map(
        toTenancyTO,
      );
      state.firstName = payload.firstName || state.firstName;
      state.lastName = payload.lastName || state.lastName;
      state.accessToken = payload.accessToken || state.accessToken;
      state.clinicianId = payload.clinicianId || state.clinicianId;
      state.message = payload.message || state.message;
      state.teams = (payload.teams || [])?.map(toTeamTO) || [];
      state.allTenancyClinics = payload.clinics || [];
      const selectedTenancy = toTenancyTO(
        payload.selectedTenancy || state.selectedTenancy || state.tenancies[0],
      );
      const filteredClinics = filterClinicsByTenancy(
        payload.clinics || [],
        selectedTenancy.tenancy_id,
      );
      state.selectedTenancy = selectedTenancy;
      state.clinics = filteredClinics;
      state.primaryTeams = getPrimaryTeamsFromClinics(filteredClinics);
      state.healthProfessionals =
        getHealthProfessionalsFromClinics(filteredClinics);
      state.clinicianProfileStatus = true;
      state.dashSettings = payload.dashSettings
        ? JSON.parse(payload.dashSettings)
        : state.dashSettings;
      state.allUsers = payload.allUsers || state.allUsers || {};
      state.permissionsSetup =
        payload.permissionsSetup || state.permissionsSetup || false;
      state.userPermissions =
        payload.userPermissions || state.userPermissions || {};
      state.details = payload.details || state.details || {};
    },
    setClinicianTenancy: (
      state,
      { payload }: { payload: TenancyTO | null },
    ) => {
      if (!payload) {
        return;
      }
      state.selectedTenancy = payload || state.selectedTenancy;
      const filteredClinics = filterClinicsByTenancy(
        state.allTenancyClinics,
        payload.tenancy_id,
      );
      state.clinics = filteredClinics;
    },
    setDashSettings: (
      state,
      { payload }: { payload: Partial<ClinicianProfileState> },
    ) => {
      state.dashSettings = payload;
    },
    setClinicianTeams: (state, { payload }: { payload: TeamTO[] }) => {
      state.teams = (payload || [])?.map(toTeamTO) || [];
    },
  },
});

export const {
  checkingClinicianProfileStatus,
  setClinicianProfile,
  setClinicianTenancy,
  setDashSettings,
  setClinicianTeams,
} = clinicianProfileBasicSlice.actions;

export default clinicianProfileBasicSlice.reducer;
